/** How an operation ends (04 §2.4): victory, defeat, withdrawal - and who goes home to the Hall. */

import { Pcg32 } from '../../sim/rng';
import { generateRecruit } from '../company';
import { hazardRules, MAX_HAZARD } from '../content/career';
import { COMMENDATIONS, COMPANY, KEYSTONE_FAILURES_ALLOWED } from '../content/economy';
import { openNodes } from '../map';
import { fullName, isAlive, newRangerId, type OperationState, type ProfileState } from '../state';
import type { RangerRecord } from '../types';
import { applyMerit } from './merit';
import { loseCaptives } from './roster';

/** Enlist a generated replacement; the caller has checked the price and the cap. */
export function enlist(profile: ProfileState, op: OperationState, recruit?: RangerRecord): RangerRecord {
  const id = newRangerId(profile);
  const rng = new Pcg32(op.seed, 0x0906).fork(id);
  const taken = new Set(op.company.map((r) => r.callsign));
  const ranger = recruit ? { ...recruit, id } : generateRecruit(rng, id, taken, hazardRules(op.hazard));
  op.company.push(ranger);
  return ranger;
}

/** Close the books: captives are lost, surviving veterans return to the Hall they came from. */
export function closeOperation(profile: ProfileState, op: OperationState, status: 'won' | 'lost' | 'withdrawn'): void {
  op.status = status;
  op.deployed = null;
  loseCaptives(profile, op, 'The operation ended before a rescue could be mounted.');
  for (const vet of op.company.filter((r) => r.veteran && isAlive(r))) {
    if (status === 'won') applyMerit(vet, 0, true, true);
    profile.hall.push({ ...vet, status: 'ready', fatigue: 0, recoveryMissions: 0 });
  }
  op.company = op.company.filter((r) => !(r.veteran && isAlive(r)));
  if (status !== 'won') return;
  // COL is campaign-only: a won operation is what finally confers it (02 §11).
  for (const ranger of op.company.filter(isAlive)) applyMerit(ranger, 0, true, true);
  profile.operationsWon += 1;
  profile.commendations += COMMENDATIONS.operationWon + COMMENDATIONS.perDifficulty * op.difficulty;
  if (op.hazard >= profile.hazardUnlocked && profile.hazardUnlocked < MAX_HAZARD) profile.hazardUnlocked += 1;
  op.log.push(`Operation ${op.codename} is a victory.`);
}

/**
 * Win and loss detection, run after every change to the run. When nobody can deploy, HQ
 * sends a replacement if the company can pay for one - so the run never soft-locks - and
 * the operation is lost when it cannot (04 §2.4).
 */
export function settleOperation(profile: ProfileState, op: OperationState): void {
  if (op.status !== 'active') return;
  const keystones = op.nodes.filter((n) => n.keystone);
  if (keystones.length > 0 && keystones.every((n) => n.state === 'done')) {
    closeOperation(profile, op, 'won');
    return;
  }
  if (op.keystoneFailures >= KEYSTONE_FAILURES_ALLOWED) {
    op.log.push('The keystone held twice. The operation is called off.');
    closeOperation(profile, op, 'lost');
    return;
  }
  if (op.deployed !== null || op.company.some((r) => r.status === 'ready')) return;
  // A Rest & Refit or a Replacement Depot within reach may yet put someone on the aircraft.
  const recovering = op.company.some((r) => r.status === 'wounded' || r.status === 'critical' || r.status === 'mia');
  if (openNodes(op).some((n) => n.kind === 'depot' || (n.kind === 'rest' && recovering))) return;
  if (op.rp >= COMPANY.recruitCost && op.company.length < COMPANY.cap) {
    op.rp -= COMPANY.recruitCost;
    const recruit = enlist(profile, op);
    op.log.push(`No ranger can deploy: HQ sends ${fullName(recruit)} (-${COMPANY.recruitCost} RP).`);
    return;
  }
  op.log.push('No ranger can deploy and no replacement can be paid for.');
  closeOperation(profile, op, 'lost');
}
